import { WorkflowRunStep, WorkflowRunStepLoop } from 'dashboard/models/workflow-step';
import { ApiModel } from 'lib/interfaces/api';
import { TokensToNode, WorkflowStepTreeNode } from 'lib/workflows';

const getLoopChildTokens = (node: WorkflowStepTreeNode): string[] => {
  if (!node.children || !node.children.length) {
    return [];
  } else {
    return [
      ...node.children.map((childNode) => childNode.step.token),
      ...node.children.flatMap((childNode) => getLoopChildTokens(childNode)),
    ];
  }
};

/**
 * @param runSteps array of workflow run steps
 * @returns hash of loop step token to displayed cycle
 */
export const getLoopTokensToCycleIdx = (runSteps: WorkflowRunStep[]): Record<string, number> => {
  const loopRunSteps = runSteps.filter(
    (step): step is WorkflowRunStepLoop => step.type === ApiModel.WorkflowRunStepLoop,
  );

  return loopRunSteps.reduce((result: Record<string, number>, step) => {
    result[step.attributes['config-step-token']] = step.attributes['errored-cycles'].length
      ? step.attributes['errored-cycles'][0]
      : 0;
    return result;
  }, {});
};

/**
 * @param runSteps array of workflow run steps
 * @param loopTokensToCycleIdx result of getLoopTokensToCycleIdx
 * @param nodeGraph result of buildGraph
 * @returns hash of step config tokens to workflow run steps
 */
export const getTokensToRunSteps = (
  loopTokensToCycleIdx: Record<string, number>,
  nodeGraph: TokensToNode,
  runSteps?: WorkflowRunStep[],
): Record<string, WorkflowRunStep> => {
  if (!runSteps?.length) {
    return {};
  }

  const loopRunSteps = runSteps.filter(
    (step): step is WorkflowRunStepLoop => step.type === ApiModel.WorkflowRunStepLoop,
  );

  // mapping of loop child step tokens to cycle
  const runStepTokenToOrder = loopRunSteps.reduce<Record<string, number>>((hash, step) => {
    for (const [cycle, stepTokens] of Object.entries(step.attributes['cycle-to-step-tokens'])) {
      stepTokens.forEach((stepToken) => (hash[stepToken] = parseInt(cycle)));
    }
    return hash;
  }, {});

  // mapping of loop child step tokens to current loop index
  const tokenToRunStepIdx = loopRunSteps.reduce<Record<string, number>>((hash, loopStep) => {
    const treeNode: WorkflowStepTreeNode | null =
      nodeGraph[loopStep.attributes['config-step-token']] ?? null;
    if (treeNode == null) return {};

    getLoopChildTokens(treeNode).forEach((childToken) => {
      hash[childToken] = loopTokensToCycleIdx[loopStep.attributes['config-step-token']];
    });
    return hash;
  }, {});

  // mapping of step config tokens to array of ordered corresponding workflow steps
  const tokensToSteps = runSteps.reduce<Record<string, WorkflowRunStep[]>>((mapping, step) => {
    const configToken: string = step.attributes['config-step-token'];
    if (step.id in runStepTokenToOrder) {
      if (!(configToken in mapping)) {
        mapping[configToken] = [];
      }
      mapping[configToken][runStepTokenToOrder[step.id]] = step;
    } else {
      mapping[configToken] = [step];
    }
    return mapping;
  }, {});

  const result: Record<string, WorkflowRunStep> = {};
  Object.keys(tokensToSteps).forEach((token) => {
    result[token] =
      token in tokenToRunStepIdx
        ? tokensToSteps[token][tokenToRunStepIdx[token]]
        : tokensToSteps[token][0];
  });

  return result;
};
